import {
  resolveWavesTerrain,
  terrainWalls,
  type WavesChart,
  type WavesObstacle,
  type WavesTerrain,
  type WavesTerrainWall,
} from '../../wavesChart.js';

/**
 * Reason a Waves run ended in failure. Currently only one — collision
 * with a gate edge or saw body. Kept as a string union so a future
 * "out of bounds" failure (e.g. player flew off-screen) can extend it
 * without widening downstream renderers.
 */
export type WavesDeathReason = 'collided';

export type WavesPassVerdict = 'perfect' | 'good';

/**
 * Waves-mode resolution event: one obstacle the player passed alive.
 * Mirrors Pulse's `PadResolution` shape so the shared `LoopRunState`
 * star/cup tracker can poll resolutions for milestone progress without
 * caring which mode produced them.
 */
export interface WavesResolution {
  obstacleId: string;
  /** Beat at which the obstacle reached the player's X. */
  beat: number;
  /** Vertical distance from the player to the nearest fatal edge. */
  clearanceY: number;
  /** How centred the pass was through the gap. */
  verdict: WavesPassVerdict;
}

export interface WavesEngineState {
  beat: number;
  /**
   * True iff the input button is currently held. Flips to true on
   * 'down' events and back to false on 'up' events; the player's Y
   * velocity is `-vYPerBeat` while held (toward CEILING_Y) and
   * `+vYPerBeat` while released (toward FLOOR_Y).
   */
  pressing: boolean;
  /**
   * Beat at which `pressing` most recently changed. The player's Y
   * since that beat is computed as
   *   `lastEdgeY + (pressing ? -1 : +1) × vYPerBeat × (beat - lastEdgeBeat)`
   * which is the engine's piecewise-linear path model.
   */
  lastEdgeBeat: number;
  /** Player Y at {@link lastEdgeBeat}. */
  lastEdgeY: number;
  /**
   * Player's current Y position. Kept in sync with the piecewise-linear
   * trajectory at the latest tick boundary so renderers can sample
   * without re-integrating. Engine writes this; nobody else should.
   */
  playerY: number;

  alive: boolean;
  completed: boolean;
  deathReason: WavesDeathReason | null;
  deathBeat: number | null;
  /** Obstacle id that killed the player; null if alive or completed cleanly. */
  collisionObstacleId: string | null;

  // Scoring + progression
  /**
   * Cumulative score. Each obstacle passed alive grants
   * {@link WAVES_OBSTACLE_PASS_SCORE} × `comboMultiplier(combo + 1)`.
   * Persists across loops so the results overlay can show a run total.
   */
  score: number;
  /**
   * Number of obstacles passed without a death since the last reset.
   * Death resets it to 0; clean loop completion preserves it so a
   * deep-loop run can accumulate big combo multipliers.
   */
  combo: number;
  maxCombo: number;
  /**
   * Pass verdict counters for the shared ReplayOutcome accuracy shape.
   * Perfect = centred in the gap; good = cleared but grazed an edge.
   */
  perfects: number;
  goods: number;
  oks: number;
  misses: number;
  /**
   * Obstacles whose beat has been crossed alive. Stays populated for
   * the lifetime of a loop so `step()` can idempotently award score
   * even if it's re-fed the same `(fromBeat, toBeat)` window across
   * server / client re-sims.
   */
  passedObstacleIds: Set<string>;
  /**
   * Lethal terrain walls the ship has crossed alive. Mirrors
   * {@link passedObstacleIds} so re-fed `(fromBeat, toBeat)` windows
   * don't re-test a wall against the ship's stale post-pass Y.
   */
  passedWallIds: Set<string>;
  resolutions: WavesResolution[];
}

/** A lethal wall with a stable id for pass-tracking + death attribution. */
export interface WavesConfigWall extends WavesTerrainWall {
  id: string;
}

export interface WavesEngineConfig {
  chart: WavesChart;
  /**
   * Vertical speed (playfield units per beat) the player moves while
   * the button is held. Same magnitude on release, opposite sign.
   * Mirrored from `chart.playerVyPerBeat` at config-build time so
   * the engine doesn't re-read a chart field every tick.
   */
  vYPerBeat: number;
  /** Hit-test radius (playfield units). Mirrored from chart.playerRadius. */
  playerRadius: number;
  /**
   * Resolved corridor terrain (the chart's, or a flat full corridor for
   * legacy charts). The glide-clamp reads `floor`/`ceiling` heights;
   * vertical {@link walls} are the lethal faces.
   */
  terrain: WavesTerrain;
  /** Lethal vertical walls, sorted by beat ascending, with stable ids. */
  walls: WavesConfigWall[];
}

export function buildWavesEngineConfig(chart: WavesChart): WavesEngineConfig {
  // Canonicalise obstacle order so the engine's early-break-on-beat
  // loop is correct even if a user-uploaded chart shuffles the array.
  // The validator's sort guard runs against a copy, so the original
  // ordering is not enforced on the wire — defending the engine here
  // ensures shuffled input can't silently skip every obstacle past
  // the first out-of-order entry.
  const sortedObstacles = [...chart.obstacles].sort((a, b) => a.beat - b.beat);
  const canonicalChart =
    sortedObstacles.every((o, i) => o === chart.obstacles[i])
      ? chart
      : { ...chart, obstacles: sortedObstacles };
  const terrain = resolveWavesTerrain(canonicalChart);
  const walls: WavesConfigWall[] = terrainWalls(terrain).map((wall, i) => ({
    ...wall,
    id: `wall-${i.toString().padStart(4, '0')}`,
  }));
  return {
    chart: canonicalChart,
    vYPerBeat: chart.playerVyPerBeat,
    playerRadius: chart.playerRadius,
    terrain,
    walls,
  };
}

export function createInitialWavesState(chart: WavesChart): WavesEngineState {
  return {
    beat: 0,
    pressing: false,
    lastEdgeBeat: 0,
    lastEdgeY: chart.spawnY,
    playerY: chart.spawnY,
    alive: true,
    completed: false,
    deathReason: null,
    deathBeat: null,
    collisionObstacleId: null,
    score: 0,
    combo: 0,
    maxCombo: 0,
    perfects: 0,
    goods: 0,
    oks: 0,
    misses: 0,
    passedObstacleIds: new Set(),
    passedWallIds: new Set(),
    resolutions: [],
  };
}

/**
 * Reset chart-relative parts of a Waves state for the next infinite-mode
 * loop. Score / combo / maxCombo / perfects accumulate so the results
 * overlay can show the run total; everything else returns to spawn.
 *
 * Mutates the state in place — same contract as
 * {@link import('../pulse/state.js').resetEngineStateForNextLoop}.
 */
export function resetWavesStateForNextLoop(
  state: WavesEngineState,
  chart: WavesChart,
): void {
  state.beat = 0;
  state.pressing = false;
  state.lastEdgeBeat = 0;
  state.lastEdgeY = chart.spawnY;
  state.playerY = chart.spawnY;
  state.alive = true;
  state.completed = false;
  state.deathReason = null;
  state.deathBeat = null;
  state.collisionObstacleId = null;
  state.passedObstacleIds = new Set();
  state.passedWallIds = new Set();
  state.resolutions = [];
  // Note: score, combo, maxCombo, perfects intentionally preserved —
  // they accumulate across loops.
}

/**
 * Sort obstacles by beat ascending. Caller owns the returned array so
 * callers building lookups across multiple frames can rebuild once at
 * config time.
 */
export function buildWavesObstacleLookup(
  obstacles: ReadonlyArray<WavesObstacle>,
): WavesObstacle[] {
  return [...obstacles].sort((a, b) => a.beat - b.beat);
}
